import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import FooterWithNewsletter from '@/components/ui/footer-column';
import SEO from '@/components/SEO';
import { useSEO } from '@/hooks/useSEO';
import { getSEOConfigForPage } from '@/utils/seoUtils';
import { AdGallery } from '@/components/ui/ad-gallery';
import { ClientLogosSlider } from '@/components/ui/ClientLogosSlider';
import { Glow } from '@/components/ui/glow';
import { ShareSidebar } from '@/components/ui/share-sidebar';
import { useSearchParams, useNavigate } from 'react-router-dom';
interface AdItem {
  id: string;
  title: string;
  description?: string;
  logoUrl: string;
  youtubeId: string;
  industry?: string;
  clientType?: string;
  url_slug?: string;
  // New fields derived from API:
  // - name2 => type ('video' | 'image')
  // - image (comma separated) => images[] for image type
  type?: 'video' | 'image';
  images?: string[];
}
const AIAdGalleryPage = () => {
  const shouldReduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get dynamic ad data based on slug parameter
  const getDynamicAdData = () => {
    // Only return dynamic data if we have a specific slug AND the ad is found
    if (slugParam && apiAds.length > 0) {
      const adBySlug = apiAds.find(ad => ad.url_slug === slugParam);
      if (adBySlug) {
        // Extract video ID from various YouTube URL formats
        let videoId = adBySlug.youtubeId;
        if (adBySlug.youtubeId.includes('/embed/')) {
          videoId = adBySlug.youtubeId.split('/embed/')[1]?.split('?')[0] || adBySlug.youtubeId;
        } else if (adBySlug.youtubeId.includes('youtube.com/watch?v=')) {
          videoId = adBySlug.youtubeId.split('v=')[1]?.split('&')[0] || adBySlug.youtubeId;
        } else if (adBySlug.youtubeId.includes('youtu.be/')) {
          videoId = adBySlug.youtubeId.split('youtu.be/')[1]?.split('?')[0] || adBySlug.youtubeId;
        } else if (adBySlug.youtubeId.includes('youtube.com/shorts/')) {
          videoId = adBySlug.youtubeId.split('shorts/')[1]?.split('?')[0] || adBySlug.youtubeId;
        }
        return {
          title: adBySlug.title,
          description: adBySlug.description,
          imageUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        };
      }
    }

    // Return null if no specific slug - this will use page-level SEO
    return null;
  };
  const {
    fallbackMetadata
  } = getSEOConfigForPage('/ai-ad-gallery');
  const {
    seoData,
    canonicalUrl,
    noindex,
    nofollow
  } = useSEO({
    fallbackMetadata
  });

  // API state
  const [apiAds, setApiAds] = useState<AdItem[]>([]);
  const [selectedAd, setSelectedAd] = useState<AdItem | null>(null);

  // URL parameter handling
  const slugParam = searchParams.get('slug');

  // API URL setup like DailyCircusPage
  let apiUrl = "https://api.gimmefy.ai/v2";
  const baseUrl = window.location.origin;
  if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
    apiUrl = "https://apidev.gimmefy.ai/v2";
  } else if (baseUrl.includes("staging.gimmefy.ai")) {
    apiUrl = "https://apistaging.gimmefy.ai/v2";
  }

  // Fetch content API
  const fetchAds = async () => {
    try {
      const contentUrl = `${apiUrl}/contents/public?filters[group_name]=WEBSITE_AI_AD_GALLERY&sort=display_order`;
      const response = await fetch(contentUrl);
      const data = await response.json();
      if (data.success && Array.isArray(data.payload)) {
        // Filter: only show items where categories === "1" or categories === 1
        const filteredPayload = data.payload.filter((item: Record<string, unknown>) => {
          return item.categories === "1" || item.categories === 1;
        });
        const transformedAds: AdItem[] = filteredPayload.map((item: Record<string, unknown>, index: number) => {
          const rawType = (item.name2 as string || '').toLowerCase();
          const isImageType = rawType === 'image';
          const rawImages = item.image as string || '';
          const imageList = rawImages ? rawImages.split(',').map(img => img.trim()).filter(Boolean) : [];
          const content2 = item.content2 as string || '';

          // For video-type ads, derive YouTube embed URL from content2.
          // For image-type ads, there is no video, so leave youtubeId empty.
          let youtubeId = '';
          if (!isImageType && content2) {
            youtubeId = content2;
            if (content2.includes('youtube.com/watch?v=')) {
              const videoId = content2.split('v=')[1]?.split('&')[0];
              if (videoId) {
                youtubeId = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&autoplay=1`;
              }
            } else if (content2.includes('youtu.be/')) {
              // Handle youtu.be short URLs
              const videoId = content2.split('youtu.be/')[1]?.split('?')[0];
              if (videoId) {
                youtubeId = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&autoplay=1`;
              }
            } else if (content2.includes('youtube.com/shorts/')) {
              // Handle YouTube Shorts URLs
              const videoId = content2.split('shorts/')[1]?.split('?')[0];
              if (videoId) {
                youtubeId = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&autoplay=1`;
              }
            } else if (!content2.includes('/embed/')) {
              // If it's not an embed URL and not a watch URL, assume it's a video ID
              youtubeId = `https://www.youtube-nocookie.com/embed/${content2}?rel=0&modestbranding=1&controls=1&autoplay=1`;
            }
          }
          const logoFromApi = item.image as string || '';
          const logoUrl = isImageType ? imageList[0] || '/lovable-uploads/placeholder.svg' : logoFromApi || '/lovable-uploads/placeholder.svg';
          const transformed: AdItem = {
            id: String(item.id ?? `ad-${index + 1}`),
            title: item.name as string || 'Untitled Ad',
            description: item.content as string || 'No description available',
            logoUrl,
            youtubeId,
            industry: item.tags as string || 'General',
            clientType: item.categories as string || 'Unknown',
            url_slug: item.url_slug as string || `ad-${index + 1}`,
            type: isImageType ? 'image' : 'video',
            images: imageList.length ? imageList : undefined
          };
          return transformed;
        });
        setApiAds(transformedAds);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };
  useEffect(() => {
    fetchAds();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle slug parameter to open specific ad
  useEffect(() => {
    if (slugParam && apiAds.length > 0) {
      const adBySlug = apiAds.find(ad => {
        return ad.url_slug === slugParam;
      });
      if (adBySlug) {
        // Open the ad popup - this will be handled by the AdGallery component
        // We'll pass the slug to the component and let it handle the popup opening
      }
    }
  }, [slugParam, apiAds]);

  // Sample ad data showcasing AI-generated video advertisements
  const sampleAds = [{
    id: '1',
    title: 'TechCorp App Launch Campaign',
    description: 'Revolutionary mobile app launch featuring AI-powered productivity tools',
    logoUrl: '/lovable-uploads/3bcd8ec8-f00b-4df7-acd8-623807ecb00c.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Technology',
    clientType: 'Startup'
  }, {
    id: '2',
    title: 'LuxeFashion Brand Story',
    description: 'Premium fashion brand showcase highlighting sustainable luxury',
    logoUrl: '/lovable-uploads/74da15ff-3ab9-4eee-8b91-1255f3e7ad9b.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Fashion',
    clientType: 'Enterprise'
  }, {
    id: '3',
    title: 'MediCare+ Health Services',
    description: 'Healthcare innovation campaign promoting telemedicine solutions',
    logoUrl: '/lovable-uploads/9696e603-d972-444b-9d5d-0d432bcc410d.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Healthcare',
    clientType: 'SMB'
  }, {
    id: '4',
    title: 'ShopSmart E-commerce Platform',
    description: 'Next-generation e-commerce platform featuring AI recommendations',
    logoUrl: '/lovable-uploads/a5e90b42-3f09-4d94-8e52-da5b8640a455.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Retail',
    clientType: 'SMB'
  }, {
    id: '5',
    title: 'FinanceFlow Digital Banking',
    description: 'Modern digital banking solution with advanced security features',
    logoUrl: '/lovable-uploads/be1816ae-5bdb-49f2-b6cd-d41288611476.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Finance',
    clientType: 'Enterprise'
  }, {
    id: '6',
    title: 'TasteBud Local Delivery',
    description: 'Community-focused food delivery app supporting local restaurants',
    logoUrl: '/lovable-uploads/c1921205-3789-4e41-8812-4fa7b0c2f6f8.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Food & Beverage',
    clientType: 'Local Business'
  }, {
    id: '7',
    title: 'FitLife Wellness Platform',
    description: 'Comprehensive fitness and wellness platform with AI coaching',
    logoUrl: '/lovable-uploads/ccc4c6c9-e951-44ab-9bc0-a2824d7576a0.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Health & Fitness',
    clientType: 'SMB'
  }, {
    id: '8',
    title: 'CloudSync Enterprise Solutions',
    description: 'Enterprise cloud management platform with advanced analytics',
    logoUrl: '/lovable-uploads/d3ea831e-2d45-48c7-a5c6-13de16d3fd56.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Technology',
    clientType: 'Enterprise'
  }, {
    id: '9',
    title: 'WanderLust Travel Experiences',
    description: 'Curated travel experiences platform with VR previews',
    logoUrl: '/lovable-uploads/e64282f4-5403-4f77-813c-4b5543edffc4.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Travel & Tourism',
    clientType: 'Agency'
  }, {
    id: '10',
    title: 'EduTech Learning Hub',
    description: 'Interactive learning platform with AI tutoring capabilities',
    logoUrl: '/lovable-uploads/f7da40e0-a08d-4254-933e-90d990e19bf1.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Education',
    clientType: 'Startup'
  }, {
    id: '11',
    title: 'PropertyPro Real Estate',
    description: 'Smart real estate platform with virtual property tours',
    logoUrl: '/lovable-uploads/fc244a57-e365-4161-a28b-067685672638.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Real Estate',
    clientType: 'Agency'
  }, {
    id: '12',
    title: 'EcoDrive Automotive',
    description: 'Sustainable automotive solutions featuring electric vehicle technology',
    logoUrl: '/lovable-uploads/fe9da205-0705-4b2d-8575-d92dc85a6aa2.png',
    youtubeId: 'dQw4w9WgXcQ',
    industry: 'Automotive',
    clientType: 'Enterprise'
  }];
  return <>
      {seoData && <SEO data={{
      ...seoData,
      ...(getDynamicAdData() || {})
    }} canonicalUrl={canonicalUrl} noindex={noindex} nofollow={nofollow} />}
      
      <div className="relative overflow-x-hidden overflow-y-auto md:overflow-y-hidden">
        {/* Enhanced Background with Multiple Gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/98 to-background/95 -z-50" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-accent/5 -z-40" />
        <div className="absolute inset-0 bg-gradient-to-bl from-accent/3 via-transparent to-primary/3 -z-30" />
        
        {/* Glow Effects */}
        <Glow variant="top" className="-z-20 opacity-60" />
        <Glow variant="center" className="-z-20 opacity-40" />
        <Glow variant="bottom" className="-z-20 opacity-30 hidden md:block" />
        
        {/* Page-Wide Animated Background Shapes */}
        <div className="absolute inset-0 -z-5 overflow-hidden hidden md:block">
          {/* Top Section Shapes */}
          <motion.div className="absolute top-[10vh] left-[15%] w-72 h-72 bg-gradient-to-r from-primary/30 to-accent/25 rounded-full blur-xl" animate={shouldReduceMotion ? {} : {
          scale: [1, 1.3, 1],
          opacity: [0.6, 0.8, 0.6],
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }} />
          <motion.div className="absolute top-[20vh] right-[20%] w-56 h-56 bg-gradient-to-r from-accent/25 to-primary/20 rounded-full blur-lg" animate={shouldReduceMotion ? {} : {
          scale: [1.2, 1, 1.2],
          opacity: [0.5, 0.7, 0.5],
          x: [0, -25, 0],
          y: [0, 15, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }} />
          
          {/* Middle Section Shapes */}
          <motion.div className="absolute top-[40vh] left-[10%] w-40 h-40 bg-gradient-to-br from-accent/30 to-primary/25 rounded-2xl blur-xl" animate={shouldReduceMotion ? {} : {
          rotate: [0, 180, 360],
          scale: [1, 1.1, 1],
          opacity: [0.4, 0.7, 0.4]
        }} transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
          delay: 1
        }} />
          <motion.div className="absolute top-[50vh] right-[15%] w-48 h-48 bg-gradient-to-tl from-primary/25 to-accent/20 rounded-3xl blur-lg" animate={shouldReduceMotion ? {} : {
          rotate: [360, 180, 0],
          scale: [1.1, 1, 1.1],
          opacity: [0.5, 0.3, 0.5],
          x: [0, 20, 0]
        }} transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3
        }} />
          
          {/* Bottom Section Shapes */}
          <motion.div className="absolute top-[70vh] left-[25%] w-32 h-32 bg-accent/35 rounded-full blur-lg" animate={shouldReduceMotion ? {} : {
          scale: [1, 1.4, 1],
          opacity: [0.6, 0.8, 0.6],
          y: [0, -30, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 4
        }} />
          <motion.div className="absolute top-[80vh] right-[30%] w-36 h-24 bg-primary/30 rounded-xl blur-lg" animate={shouldReduceMotion ? {} : {
          scale: [1.2, 1, 1.2],
          opacity: [0.5, 0.7, 0.5],
          x: [0, -15, 0],
          rotate: [0, 45, 0]
        }} transition={{
          duration: 11,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 5
        }} />
          
          {/* Floating Micro Elements */}
          <motion.div className="absolute top-[35vh] left-[50%] w-20 h-20 bg-accent/40 rounded-full blur-md" animate={shouldReduceMotion ? {} : {
          y: [0, -40, 0],
          opacity: [0.7, 1, 0.7],
          scale: [1, 1.2, 1]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 6
        }} />
          <motion.div className="absolute top-[60vh] left-[70%] w-24 h-16 bg-primary/35 rounded-lg blur-md" animate={shouldReduceMotion ? {} : {
          x: [0, 25, 0],
          y: [0, -20, 0],
          opacity: [0.4, 0.6, 0.4],
          rotate: [0, 90, 0]
        }} transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 7
        }} />
          
          {/* Additional Scattered Elements */}
          <motion.div className="absolute top-[25vh] left-[75%] w-16 h-16 bg-gradient-to-r from-primary/25 to-accent/20 rounded-full blur-md" animate={shouldReduceMotion ? {} : {
          scale: [1, 1.3, 1],
          opacity: [0.5, 0.8, 0.5],
          rotate: [0, 180, 360]
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 8
        }} />
          <motion.div className="absolute top-[85vh] left-[40%] w-28 h-28 bg-gradient-to-br from-accent/20 to-primary/15 rounded-2xl blur-lg" animate={shouldReduceMotion ? {} : {
          scale: [1.1, 1, 1.1],
          opacity: [0.3, 0.6, 0.3],
          x: [0, -30, 0],
          y: [0, 20, 0]
        }} transition={{
          duration: 16,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 9
        }} />
        </div>
        
        <Navigation />
        
        {/* Enhanced Hero Section */}
        <section className="relative pt-20 pb-16">
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-6xl mx-auto">
              {/* Enhanced Title with Better Gradients */}
              <motion.h1 initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="text-4xl md:text-6xl lg:text-8xl font-bold mb-8 leading-tight tracking-tight text-foreground">
                AI-Powered Ad
                <br />
                Showcase
              </motion.h1>

              {/* Enhanced Description with Better Typography */}
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.4
            }} className="text-xl md:text-2xl lg:text-3xl text-foreground/90 max-w-5xl mx-auto mb-8 leading-relaxed font-medium">
                Experience the smarter way to craft ads: High-impact, on-brand concepts, visuals and videos created efficiently with{" "}
                <span className="text-primary font-semibold">gimmefy</span>.
              </motion.p>

              {/* Subtle Backdrop Overlay for Text Readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/10 to-background/20 rounded-3xl -z-15" />
            </div>
          </div>
        </section>

        {/* Client Logos Section */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.8
      }} className="relative mb-16">
          <ClientLogosSlider />
        </motion.section>

        {/* Gallery Section */}
        <section className="relative pb-16">
          <div className="container mx-auto px-4">
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.2
          }}>
              <AdGallery ads={apiAds.length > 0 ? apiAds : sampleAds} slugParam={slugParam} navigate={navigate} onAdSelect={setSelectedAd} />
            </motion.div>
          </div>
        </section>
        
        <FooterWithNewsletter />
      </div>

      {/* Share Sidebar - Only shows when popup is open */}
      <ShareSidebar isOpen={!!selectedAd} shareUrl={`${window.location.origin}/ai-ad-gallery?slug=${selectedAd?.url_slug || `ad-${selectedAd?.id}`}`} shareTitle={selectedAd?.title || 'AI Ad Gallery'} shareDescription={selectedAd?.description || 'Check out this amazing AI-generated advertisement!'} />
    </>;
};
export default AIAdGalleryPage;